import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronDown, MapPin, Plus, Check, Truck, Zap, User, X, CreditCard, Tag } from 'lucide-react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import Navbar from './Navbar';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Country codes for phone numbers
const COUNTRY_CODES = [
  { code: '+31', country: 'NL', flag: '🇳🇱' },
  { code: '+32', country: 'BE', flag: '🇧🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+44', country: 'GB', flag: '🇬🇧' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+48', country: 'PL', flag: '🇵🇱' },
  { code: '+46', country: 'SE', flag: '🇸🇪' },
  { code: '+43', country: 'AT', flag: '🇦🇹' },
  { code: '+41', country: 'CH', flag: '🇨🇭' },
  { code: '+352', country: 'LU', flag: '🇱🇺' },
  { code: '+1', country: 'US', flag: '🇺🇸' },
  { code: '+966', country: 'SA', flag: '🇸🇦' },
  { code: '+971', country: 'AE', flag: '🇦🇪' },
];

// Payment methods with real logos
const PAYMENT_METHODS = [
  { 
    id: 'ideal', 
    name: 'iDEAL', 
    logo: 'https://www.ideal.nl/img/logo/ideal-logo-1024.png'
  },
  { 
    id: 'card', 
    name: 'Creditcard', 
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/200px-Mastercard-logo.svg.png',
    description: 'Mastercard, Visa' 
  },
  { 
    id: 'klarna', 
    name: 'Klarna', 
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/40/Klarna_Payment_Badge.svg/200px-Klarna_Payment_Badge.svg.png',
    description: 'Achteraf betalen' 
  },
  { 
    id: 'applepay', 
    name: 'Apple Pay', 
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b0/Apple_Pay_logo.svg/200px-Apple_Pay_logo.svg.png'
  },
];

// Parse address to separate street from house number
const parseAddress = (fullAddress) => {
  if (!fullAddress) return { street: '', houseNumber: '', houseNumberExt: '' };
  
  // Regex to match Dutch addresses: "Straatnaam 123" or "Straatnaam 123a" or "Straatnaam 123-A"
  const match = fullAddress.match(/^(.+?)\s+(\d+)\s*([a-zA-Z\-\/]?\d*[a-zA-Z]?)?$/);
  
  if (match) {
    return {
      street: match[1].trim(),
      houseNumber: match[2],
      houseNumberExt: match[3] || ''
    };
  }
  
  // If no match, check if it contains numbers at all
  const hasNumber = /\d/.test(fullAddress);
  if (!hasNumber) {
    return { street: fullAddress, houseNumber: '', houseNumberExt: '' };
  }
  
  return { street: fullAddress, houseNumber: '', houseNumberExt: '' };
};

// Validate street doesn't contain numbers
const validateStreet = (value) => {
  return value.replace(/\d/g, '').trim();
};

const Checkout = () => {
  const navigate = useNavigate();
  const { t, API, token, user, cart, getCartTotal, clearCart, language } = useApp();
  
  // Address state
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNewAddress, setShowNewAddress] = useState(false);
  
  // Guest checkout state
  const [isGuest, setIsGuest] = useState(!token);
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);
  
  // Shipping option state
  const [shippingMethod, setShippingMethod] = useState('standard');
  
  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState('card');
  
  // Country code state
  const [selectedCountryCode, setSelectedCountryCode] = useState(COUNTRY_CODES[0]);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  
  // Guest address form with house number extension
  const [guestAddress, setGuestAddress] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    street: '',
    house_number: '',
    house_number_ext: '', // New field for extensions like 'A', 'bis', etc.
    postal_code: '',
    city: '',
    country: 'Nederland'
  });

  // New address form for logged in users
  const [newAddress, setNewAddress] = useState({
    label: '',
    street: '',
    house_number: '',
    house_number_ext: '',
    postal_code: '',
    city: '',
    country: 'Nederland',
    is_default: false
  });

  useEffect(() => {
    if (cart.length === 0) {
      navigate('/cart');
      return;
    }
    if (token) {
      setIsGuest(false);
      loadAddresses();
    }
  }, [token, cart]);

  const loadAddresses = async () => {
    try {
      const response = await axios.get(`${API}/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data.addresses);
      const defaultAddr = response.data.addresses.find(a => a.is_default);
      if (defaultAddr) {
        setSelectedAddressId(defaultAddr.id);
      } else if (response.data.addresses.length > 0) {
        setSelectedAddressId(response.data.addresses[0].id);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
    }
  };

  // Remove leading zero from phone number
  const formatPhoneNumber = (phone) => {
    let cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    return cleaned;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setGuestAddress({...guestAddress, phone: formatted});
  };

  // Handle street input - remove numbers automatically
  const handleStreetChange = (e, isGuest = true) => {
    const rawValue = e.target.value;
    const parsed = parseAddress(rawValue);
    
    // If autofill pasted a full address with number, split it
    if (parsed.houseNumber) {
      if (isGuest) {
        setGuestAddress(prev => ({
          ...prev,
          street: parsed.street,
          house_number: parsed.houseNumber,
          house_number_ext: parsed.houseNumberExt || prev.house_number_ext
        }));
      } else {
        setNewAddress(prev => ({
          ...prev,
          street: parsed.street,
          house_number: parsed.houseNumber,
          house_number_ext: parsed.houseNumberExt || prev.house_number_ext
        }));
      }
    } else {
      // Just clean the street from any numbers
      const cleanStreet = validateStreet(rawValue);
      if (isGuest) {
        setGuestAddress(prev => ({...prev, street: cleanStreet}));
      } else {
        setNewAddress(prev => ({...prev, street: cleanStreet}));
      }
    }
  };

  // Validate and apply discount code
  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      toast.error('Voer een kortingscode in');
      return;
    }
    
    setDiscountLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/loyalty/validate-code?code=${discountCode.toUpperCase()}`, {}, { headers });
      
      setAppliedDiscount({
        code: response.data.code,
        amount: response.data.discount_amount
      });
      toast.success(`Kortingscode toegepast: €${response.data.discount_amount.toFixed(2)} korting!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ongeldige kortingscode');
      setAppliedDiscount(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    toast.info('Kortingscode verwijderd');
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      // Combine house number with extension
      const fullHouseNumber = newAddress.house_number_ext 
        ? `${newAddress.house_number}${newAddress.house_number_ext}`
        : newAddress.house_number;
      
      const addressPayload = {
        ...newAddress,
        house_number: fullHouseNumber
      };
      delete addressPayload.house_number_ext;
      
      const response = await axios.post(`${API}/addresses`, addressPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses([...addresses, response.data.address]);
      setSelectedAddressId(response.data.address.id);
      setShowNewAddress(false);
      setNewAddress({
        label: '',
        street: '',
        house_number: '',
        house_number_ext: '',
        postal_code: '',
        city: '',
        country: 'Nederland',
        is_default: false
      });
      toast.success('Adres toegevoegd');
    } catch (error) {
      toast.error('Adres toevoegen mislukt');
    }
  };

  const handleCheckout = async () => {
    // Validate address
    if (!isGuest && !selectedAddressId) {
      toast.error('Selecteer een bezorgadres');
      return;
    }
    
    if (isGuest) {
      // Validate guest form - phone is now required
      const required = ['email', 'first_name', 'last_name', 'phone', 'street', 'house_number', 'postal_code', 'city'];
      const missing = required.filter(field => !guestAddress[field]);
      if (missing.length > 0) {
        toast.error('Vul alle verplichte velden in (inclusief telefoonnummer)');
        return;
      }
      // Validate full name
      const namePattern = /^[a-zA-ZÀ-ÿ\s\-']+$/;
      if (guestAddress.first_name.trim().length < 2) {
        toast.error('Voer uw volledige voornaam in (minimaal 2 tekens)');
        return;
      }
      if (guestAddress.last_name.trim().length < 2) {
        toast.error('Voer uw volledige achternaam in (minimaal 2 tekens)');
        return;
      }
      if (!namePattern.test(guestAddress.first_name) || !namePattern.test(guestAddress.last_name)) {
        toast.error('Namen mogen alleen letters bevatten');
        return;
      }
      // Validate email format
      if (!/\S+@\S+\.\S+/.test(guestAddress.email)) {
        toast.error('Voer een geldig e-mailadres in');
        return;
      }
      // Validate phone number (at least 6 digits)
      if (guestAddress.phone.length < 6) {
        toast.error('Voer een geldig telefoonnummer in');
        return;
      }
    }

    setLoading(true);
    try {
      const items = cart.map(item => ({
        product_id: item.product_id,
        size_ml: item.size_ml,
        quantity: item.quantity
      }));

      // Combine house number with extension for guest checkout
      const fullHouseNumber = guestAddress.house_number_ext 
        ? `${guestAddress.house_number}${guestAddress.house_number_ext}`
        : guestAddress.house_number;

      const payload = {
        items,
        shipping_method: shippingMethod,
        payment_method: paymentMethod,
        language: language || 'nl'  // Send customer's language preference
      };

      // Add discount code if applied
      if (appliedDiscount) {
        payload.discount_code = appliedDiscount.code;
      }

      if (isGuest) {
        payload.guest_checkout = true;
        payload.guest_info = {
          ...guestAddress,
          house_number: fullHouseNumber,
          phone: `${selectedCountryCode.code}${guestAddress.phone}`
        };
        delete payload.guest_info.house_number_ext;
      } else {
        payload.address_id = selectedAddressId;
      }

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/checkout/create-session`,
        payload,
        { headers }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMessage = error.response?.data?.detail || 'Er is een fout opgetreden bij het afrekenen';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginOverlay(false);
    setIsGuest(false);
    loadAddresses();
  };

  // Calculate totals
  const subtotal = getCartTotal();
  const standardShipping = subtotal >= 50 ? 0 : 4.95;
  const expressShipping = 25;
  const shippingCost = shippingMethod === 'express' ? expressShipping : standardShipping;
  const discountAmount = appliedDiscount ? appliedDiscount.amount : 0;
  const finalTotal = Math.max(0, subtotal + shippingCost - discountAmount);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/cart')}
          className="flex items-center gap-2 text-[#A8A29E] hover:text-[#D4AF37] mb-8 transition-colors"
          data-testid="back-to-cart-btn"
        >
          <ChevronLeft size={20} />
          <span>{t('back_to_cart')}</span>
        </button>

        <h1 className="font-heading text-3xl text-white mb-8">{t('checkout')}</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Address & Shipping */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Login Prompt for Guests */}
            {isGuest && (
              <div className="glass p-4 rounded-xl border border-[#D4AF37]/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <User size={20} className="text-[#D4AF37]" />
                    <div>
                      <p className="text-white text-sm font-medium">{t('already_have_account')}</p>
                      <p className="text-[#A8A29E] text-xs">{t('login_for_saved_addresses')}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowLoginOverlay(true)}
                    className="btn-secondary text-sm px-4 py-2"
                    data-testid="login-prompt-btn"
                  >
                    {t('login')}
                  </button>
                </div>
              </div>
            )}

            {/* Address Section */}
            <div className="glass p-6 rounded-xl">
              <h2 className="font-heading text-xl text-white mb-6 flex items-center gap-2">
                <MapPin size={20} className="text-[#D4AF37]" />
                {t('delivery_address')}
              </h2>

              {/* Guest Address Form */}
              {isGuest ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder={t('first_name_required')}
                      value={guestAddress.first_name}
                      onChange={(e) => setGuestAddress({...guestAddress, first_name: e.target.value})}
                      className="input-luxury w-full"
                      required
                      autoComplete="given-name"
                      data-testid="guest-first-name"
                    />
                    <input
                      type="text"
                      placeholder={t('last_name_required')}
                      value={guestAddress.last_name}
                      onChange={(e) => setGuestAddress({...guestAddress, last_name: e.target.value})}
                      className="input-luxury w-full"
                      required
                      autoComplete="family-name"
                      data-testid="guest-last-name"
                    />
                  </div>
                  <input
                    type="email"
                    placeholder={t('email_required')}
                    value={guestAddress.email}
                    onChange={(e) => setGuestAddress({...guestAddress, email: e.target.value})}
                    className="input-luxury w-full"
                    required
                    autoComplete="email"
                    data-testid="guest-email"
                  />
                  
                  {/* Phone with Country Code */}
                  <div className="flex gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button 
                          type="button"
                          className="flex items-center gap-1 px-3 py-3 bg-transparent border-b border-[#57534E] text-white hover:border-[#D4AF37] transition-colors min-w-[90px]"
                          data-testid="country-code-dropdown"
                        >
                          <span>{selectedCountryCode.flag}</span>
                          <span className="text-sm">{selectedCountryCode.code}</span>
                          <ChevronDown size={14} className="text-[#57534E]" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1C1917] border-[#2C1B18] max-h-60 overflow-y-auto">
                        {COUNTRY_CODES.map((cc) => (
                          <DropdownMenuItem
                            key={cc.code}
                            onClick={() => setSelectedCountryCode(cc)}
                            className="text-white hover:text-[#D4AF37] hover:bg-[#2C1B18] cursor-pointer"
                          >
                            <span className="mr-2">{cc.flag}</span>
                            <span>{cc.code}</span>
                            <span className="ml-2 text-[#57534E]">{cc.country}</span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <input
                      type="tel"
                      placeholder={t('phone_required')}
                      value={guestAddress.phone}
                      onChange={handlePhoneChange}
                      className="input-luxury flex-1"
                      required
                      autoComplete="tel-national"
                      data-testid="guest-phone"
                    />
                  </div>

                  {/* Street & House Number with Extension */}
                  <div className="grid grid-cols-12 gap-4">
                    <input
                      type="text"
                      placeholder={t('street_required')}
                      value={guestAddress.street}
                      onChange={(e) => handleStreetChange(e, true)}
                      className="input-luxury col-span-12 sm:col-span-6 w-full"
                      required
                      autoComplete="address-line1"
                      data-testid="guest-street"
                    />
                    <input
                      type="text"
                      placeholder={t('house_number_required')}
                      value={guestAddress.house_number}
                      onChange={(e) => setGuestAddress({...guestAddress, house_number: e.target.value.replace(/[^\d]/g, '')})}
                      className="input-luxury col-span-6 sm:col-span-3 w-full"
                      required
                      inputMode="numeric"
                      data-testid="guest-house-number"
                    />
                    <input
                      type="text"
                      placeholder="Toev."
                      value={guestAddress.house_number_ext}
                      onChange={(e) => setGuestAddress({...guestAddress, house_number_ext: e.target.value})}
                      className="input-luxury col-span-6 sm:col-span-3 w-full"
                      maxLength={10}
                      data-testid="guest-house-number-ext"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder={t('postal_code_required')}
                      value={guestAddress.postal_code}
                      onChange={(e) => setGuestAddress({...guestAddress, postal_code: e.target.value})}
                      className="input-luxury w-full"
                      required
                      autoComplete="postal-code"
                      data-testid="guest-postal-code"
                    />
                    <input
                      type="text"
                      placeholder={t('city_required')}
                      value={guestAddress.city}
                      onChange={(e) => setGuestAddress({...guestAddress, city: e.target.value})}
                      className="input-luxury w-full"
                      required
                      autoComplete="address-level2"
                      data-testid="guest-city"
                    />
                  </div>
                </div>
              ) : (
                <>
                  {/* Existing Addresses for Logged In Users */}
                  <div className="space-y-3 mb-4">
                    {addresses.map((address) => (
                      <button
                        key={address.id}
                        onClick={() => setSelectedAddressId(address.id)}
                        className={`w-full text-left p-4 rounded-lg border transition-all ${
                          selectedAddressId === address.id
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                            : 'border-[#2C1B18] hover:border-[#D4AF37]/50'
                        }`}
                        data-testid={`address-${address.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-white font-medium">{address.label}</p>
                            <p className="text-[#A8A29E] text-sm">
                              {address.street} {address.house_number}
                            </p>
                            <p className="text-[#A8A29E] text-sm">
                              {address.postal_code} {address.city}
                            </p>
                            <p className="text-[#A8A29E] text-sm">{address.country}</p>
                          </div>
                          {selectedAddressId === address.id && (
                            <Check size={20} className="text-[#D4AF37]" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Add New Address */}
                  {!showNewAddress ? (
                    <button
                      onClick={() => setShowNewAddress(true)}
                      className="btn-secondary w-full flex items-center justify-center gap-2"
                      data-testid="add-address-btn"
                    >
                      <Plus size={18} />
                      {t('new_address')}
                    </button>
                  ) : (
                    <form onSubmit={handleAddAddress} className="space-y-4 border-t border-[#2C1B18] pt-4">
                      <h3 className="text-white font-medium">Nieuw adres</h3>
                      <input
                        type="text"
                        placeholder="Label (bijv. Thuis, Werk)"
                        value={newAddress.label}
                        onChange={(e) => setNewAddress({...newAddress, label: e.target.value})}
                        className="input-luxury w-full"
                        required
                      />
                      <div className="grid grid-cols-12 gap-4">
                        <input
                          type="text"
                          placeholder="Straat"
                          value={newAddress.street}
                          onChange={(e) => handleStreetChange(e, false)}
                          className="input-luxury col-span-12 sm:col-span-6 w-full"
                          required
                        />
                        <input
                          type="text"
                          placeholder="Nr."
                          value={newAddress.house_number}
                          onChange={(e) => setNewAddress({...newAddress, house_number: e.target.value.replace(/[^\d]/g, '')})}
                          className="input-luxury col-span-6 sm:col-span-3 w-full"
                          required
                          inputMode="numeric"
                        />
                        <input
                          type="text"
                          placeholder="Toev."
                          value={newAddress.house_number_ext}
                          onChange={(e) => setNewAddress({...newAddress, house_number_ext: e.target.value})}
                          className="input-luxury col-span-6 sm:col-span-3 w-full"
                          maxLength={10}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Postcode"
                          value={newAddress.postal_code}
                          onChange={(e) => setNewAddress({...newAddress, postal_code: e.target.value})}
                          className="input-luxury w-full"
                          required
                        />
                        <input
                          type="text"
                          placeholder="Plaats"
                          value={newAddress.city}
                          onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                          className="input-luxury w-full"
                          required
                        />
                      </div>
                      <div className="flex gap-4">
                        <button type="submit" className="btn-primary flex-1">
                          Opslaan
                        </button>
                        <button 
                          type="button"
                          onClick={() => setShowNewAddress(false)}
                          className="btn-secondary"
                        >
                          Annuleren
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>

            {/* Shipping Method Selection */}
            <div className="glass p-6 rounded-xl">
              <h2 className="font-heading text-xl text-white mb-6 flex items-center gap-2">
                <Truck size={20} className="text-[#D4AF37]" />
                {t('shipping_method')}
              </h2>

              <div className="space-y-3">
                {/* Standard Shipping */}
                <button
                  onClick={() => setShippingMethod('standard')}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    shippingMethod === 'standard'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#2C1B18] hover:border-[#D4AF37]/50'
                  }`}
                  data-testid="shipping-standard"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Truck size={24} className="text-[#A8A29E] mt-0.5" />
                      <div>
                        <p className="text-white font-medium">{t('standard_shipping')}</p>
                        <p className="text-[#A8A29E] text-sm">{t('standard_shipping_time')}</p>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <p className={`font-bold ${standardShipping === 0 ? 'text-green-500' : 'text-white'}`}>
                        {standardShipping === 0 ? t('free') : `€${standardShipping.toFixed(2)}`}
                      </p>
                      {shippingMethod === 'standard' && (
                        <Check size={20} className="text-[#D4AF37]" />
                      )}
                    </div>
                  </div>
                  {subtotal < 50 && (
                    <p className="text-[#57534E] text-xs mt-2 ml-9">{t('free_shipping_from')}</p>
                  )}
                </button>

                {/* Express Shipping - ZuiverParfum Direct Delivery */}
                <button
                  onClick={() => setShippingMethod('express')}
                  className={`w-full text-left p-4 rounded-lg border transition-all relative overflow-hidden ${
                    shippingMethod === 'express'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#D4AF37]/30 hover:border-[#D4AF37]/50'
                  }`}
                  data-testid="shipping-express"
                >
                  {/* Premium Badge */}
                  <div className="absolute top-0 right-0 bg-[#D4AF37] text-black text-xs font-bold px-2 py-0.5 rounded-bl">
                    PREMIUM
                  </div>
                  
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Zap size={24} className="text-[#D4AF37] mt-0.5" />
                      <div>
                        <p className="text-white font-medium">{t('zuiverparfum_direct')}</p>
                        <p className="text-[#D4AF37] text-sm font-medium">{t('direct_delivery_desc')}</p>
                        <p className="text-[#A8A29E] text-xs mt-1">{t('direct_delivery_info')}</p>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2 mt-4">
                      <p className="text-[#D4AF37] font-bold">€{expressShipping.toFixed(2)}</p>
                      {shippingMethod === 'express' && (
                        <Check size={20} className="text-[#D4AF37]" />
                      )}
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Payment Method Selection with Real Logos */}
            <div className="glass p-6 rounded-xl">
              <h2 className="font-heading text-xl text-white mb-6 flex items-center gap-2">
                <CreditCard size={20} className="text-[#D4AF37]" />
                {t('payment_method')}
              </h2>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {PAYMENT_METHODS.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => setPaymentMethod(method.id)}
                    className={`p-4 rounded-lg border transition-all text-center flex flex-col items-center justify-center min-h-[100px] ${
                      paymentMethod === method.id
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                        : 'border-[#2C1B18] hover:border-[#D4AF37]/50'
                    }`}
                    data-testid={`payment-${method.id}`}
                  >
                    <div className="h-10 flex items-center justify-center mb-2">
                      <img 
                        src={method.logo} 
                        alt={method.name}
                        className="max-h-10 max-w-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <span className="text-2xl hidden">{method.id === 'ideal' ? '🏦' : method.id === 'card' ? '💳' : method.id === 'klarna' ? '🛒' : '🍎'}</span>
                    </div>
                    <p className="text-white text-sm font-medium">{method.name}</p>
                    {method.description && (
                      <p className="text-[#57534E] text-xs">{method.description}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Order Items Preview */}
            <div className="glass p-6 rounded-xl">
              <h2 className="font-heading text-xl text-white mb-4">{t('order_summary')}</h2>
              <div className="space-y-3">
                {cart.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 py-2 border-b border-[#2C1B18] last:border-0">
                    <img 
                      src={item.product?.image_url}
                      alt={item.product?.name}
                      className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{item.product?.name}</p>
                      <p className="text-[#A8A29E] text-xs">{item.size_ml}ml × {item.quantity}</p>
                    </div>
                    <span className="text-[#D4AF37] flex-shrink-0">
                      €{((item.product?.sizes?.find(s => s.ml === item.size_ml)?.price || item.product?.price || 0) * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
                
                {/* Show Direct Delivery as separate line item when selected */}
                {shippingMethod === 'express' && (
                  <div className="flex items-center gap-4 py-2 border-t border-[#D4AF37]/30 mt-2 pt-3">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#D4AF37]/20 flex-shrink-0">
                      <Zap size={24} className="text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#D4AF37] text-sm font-medium">{t('zuiverparfum_direct')}</p>
                      <p className="text-[#A8A29E] text-xs">{t('delivery_time_express')}</p>
                    </div>
                    <span className="text-[#D4AF37] flex-shrink-0 font-bold">
                      €{expressShipping.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="glass p-6 rounded-xl sticky top-24">
              <h2 className="font-heading text-xl text-white mb-6">{t('order_summary')}</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-[#A8A29E]">
                  <span>{t('subtotal')}</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#A8A29E]">
                  <span>{t('shipping_method')}</span>
                  <span className={shippingCost === 0 ? 'text-green-500' : ''}>
                    {shippingCost === 0 ? t('free') : `€${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                {shippingMethod === 'express' && (
                  <div className="flex items-center gap-2 text-[#D4AF37] text-sm bg-[#D4AF37]/10 p-2 rounded-lg">
                    <Zap size={14} />
                    <span>{t('zuiverparfum_direct')} - €{expressShipping.toFixed(2)}</span>
                  </div>
                )}
                
                {/* Discount Code Section */}
                <div className="border-t border-[#2C1B18] pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag size={16} className="text-[#D4AF37]" />
                    <span className="text-white text-sm">{t('discount_code')}</span>
                  </div>
                  
                  {appliedDiscount ? (
                    <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                      <div>
                        <p className="text-green-500 text-sm font-medium">{appliedDiscount.code}</p>
                        <p className="text-green-400 text-xs">-€{appliedDiscount.amount.toFixed(2)}</p>
                      </div>
                      <button
                        onClick={handleRemoveDiscount}
                        className="text-[#A8A29E] hover:text-red-500 transition-colors"
                        data-testid="remove-discount-btn"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder={t('discount_code')}
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        className="input-luxury flex-1 text-sm py-2"
                        data-testid="discount-code-input"
                      />
                      <button
                        onClick={handleApplyDiscount}
                        disabled={discountLoading || !discountCode.trim()}
                        className="btn-secondary text-sm px-3 py-2 disabled:opacity-50"
                        data-testid="apply-discount-btn"
                      >
                        {discountLoading ? '...' : t('apply')}
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Show discount in summary */}
                {appliedDiscount && (
                  <div className="flex justify-between text-green-500">
                    <span>{t('discount_code')}</span>
                    <span>-€{appliedDiscount.amount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="border-t border-[#2C1B18] pt-4 flex justify-between text-white">
                  <span className="font-bold">{t('total')}</span>
                  <span className="font-bold text-[#D4AF37] text-xl">
                    €{finalTotal.toFixed(2)}
                  </span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pay-btn"
              >
                {loading ? (
                  <div className="spinner"></div>
                ) : (
                  <>{t('place_order')}</>
                )}
              </button>

              <p className="text-[#57534E] text-xs text-center mt-4">
                {t('secure_payment')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Login Overlay Modal */}
      {showLoginOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="glass p-8 rounded-2xl max-w-md w-full mx-4 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowLoginOverlay(false)}
              className="absolute top-4 right-4 text-[#A8A29E] hover:text-white"
            >
              <X size={24} />
            </button>
            
            <div className="text-center mb-6">
              <h1 className="font-heading text-2xl">
                <span className="gold-text">Zuiver</span>
                <span className="text-white">Parfum</span>
              </h1>
              <p className="text-[#A8A29E] text-sm mt-2">{t('login_for_faster_checkout')}</p>
            </div>
            
            <CheckoutLoginForm 
              API={API} 
              onSuccess={handleLoginSuccess}
              onClose={() => setShowLoginOverlay(false)}
            />
            
            <div className="mt-6 text-center border-t border-[#2C1B18] pt-6">
              <button
                onClick={() => setShowLoginOverlay(false)}
                className="text-[#A8A29E] hover:text-[#D4AF37] text-sm"
              >
                {t('continue_shopping')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Login Form Component for checkout with QR option
const CheckoutLoginForm = ({ API, onSuccess, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [need2FA, setNeed2FA] = useState(false);
  
  // QR Login state
  const [showQRLogin, setShowQRLogin] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [qrSessionId, setQrSessionId] = useState('');
  const [pollingQr, setPollingQr] = useState(false);

  // Generate QR code for login
  const generateQRLogin = async () => {
    try {
      const response = await fetch(`${API}/auth/qr/generate`, { method: 'POST' });
      const data = await response.json();
      setQrCode(data.qr_code);
      setQrSessionId(data.session_id);
      setShowQRLogin(true);
      setPollingQr(true);
    } catch (error) {
      toast.error('QR code genereren mislukt');
    }
  };

  // Poll for QR login status
  useEffect(() => {
    if (!pollingQr || !qrSessionId) return;
    
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API}/auth/qr/status/${qrSessionId}`);
        const data = await response.json();
        
        if (data.status === 'authorized' && data.token) {
          clearInterval(interval);
          setPollingQr(false);
          localStorage.setItem('token', data.token);
          toast.success('Ingelogd via QR code!');
          onSuccess();
          window.location.reload();
        }
      } catch (error) {
        // Session expired or not found
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, [pollingQr, qrSessionId, API, onSuccess]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, totp_code: totpCode || null })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login mislukt');
      }

      if (data.require_2fa) {
        setNeed2FA(true);
        setLoading(false);
        return;
      }

      localStorage.setItem('token', data.token);
      toast.success('Welkom terug!');
      onSuccess();
      window.location.reload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (showQRLogin) {
    return (
      <div className="text-center">
        <div className="bg-white p-4 rounded-xl inline-block mb-4">
          {qrCode && <img src={qrCode} alt="QR Login Code" className="w-48 h-48" />}
        </div>
        <p className="text-[#A8A29E] text-sm mb-4">
          Scan deze QR code met je telefoon waar je al bent ingelogd
        </p>
        <div className="flex items-center justify-center gap-2 text-[#D4AF37] text-sm">
          <div className="spinner-small"></div>
          <span>Wachten op autorisatie...</span>
        </div>
        <button
          onClick={() => {
            setShowQRLogin(false);
            setPollingQr(false);
          }}
          className="text-[#A8A29E] hover:text-white text-sm mt-4"
        >
          ← Terug naar inloggen
        </button>
      </div>
    );
  }

  return (
    <div>
      {!need2FA ? (
        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="email"
            placeholder="E-mailadres"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-luxury w-full"
            required
            autoComplete="email"
          />
          <input
            type="password"
            placeholder="Wachtwoord"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-luxury w-full"
            required
            autoComplete="current-password"
          />
          
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? <div className="spinner mx-auto"></div> : 'Inloggen'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleLogin} className="space-y-4">
          <div className="text-center">
            <p className="text-[#A8A29E] text-sm mb-4">
              Voer je 2FA code in van Microsoft Authenticator
            </p>
            <input
              type="text"
              placeholder="000000"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="input-luxury w-full text-center text-2xl tracking-widest"
              maxLength={6}
              required
              autoComplete="one-time-code"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? <div className="spinner mx-auto"></div> : 'Verifiëren'}
          </button>

          <button
            type="button"
            onClick={() => setNeed2FA(false)}
            className="text-[#A8A29E] hover:text-white text-sm w-full"
          >
            ← Terug
          </button>
        </form>
      )}

      {!need2FA && (
        <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#2C1B18]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-[#1C1917] px-4 text-[#57534E]">of</span>
            </div>
          </div>

          <button
            onClick={generateQRLogin}
            className="btn-secondary w-full flex items-center justify-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="5" height="5" x="3" y="3" rx="1"/><rect width="5" height="5" x="16" y="3" rx="1"/><rect width="5" height="5" x="3" y="16" rx="1"/><path d="M21 16h-3a2 2 0 0 0-2 2v3"/><path d="M21 21v.01"/><path d="M12 7v3a2 2 0 0 1-2 2H7"/><path d="M3 12h.01"/><path d="M12 3h.01"/><path d="M12 16v.01"/><path d="M16 12h1"/><path d="M21 12v.01"/><path d="M12 21v-1"/>
            </svg>
            Inloggen met QR code
          </button>

          <p className="text-[#57534E] text-xs text-center mt-4">
            Scan de QR code met je telefoon waar je al bent ingelogd
          </p>
        </>
      )}
    </div>
  );
};

export default Checkout;
